<template>
    <div class='partner-type-details'>
      <dean-two-column-layout :title="headerTitle">
        <dean-section-title-item
                :icon="saveIcon"
                slot="title-items"
                :content="true"
                :large="true"
                @click.native="save"
        >
          Opslaan
        </dean-section-title-item>
        <div class="column" slot="left">
          <dean-form-row id="name" label="naam" v-model="data.name" type="text" :required-field="true"
                         :warning="$v.data.name.$error" @focus="$v.data.name.$touch()"/>
          <dean-form-row id="code" label="code" v-model="data.code" type="text" :required-field="true"
                         :warning="$v.data.code.$error" @focus="$v.data.code.$touch()"/>
        </div>
        <div class="column" slot="right">
          <dean-input-switch-form-row key="ownsAgreement" id="ownsAgreement" label="heeft klantrelatie"
                                      :value="data.ownsAgreement" @checked="inputSwitchChanged"/>
          <dean-input-switch-form-row key="billsCustomer" id="billsCustomer" label="factureert klanten"
                                      :value="data.billsCustomer" @checked="inputSwitchChanged"/>
          <dean-input-switch-form-row key="suppliesHardware" id="suppliesHardware" label="levert hardware"
                                      :value="data.suppliesHardware" @checked="inputSwitchChanged"/>
          <dean-input-switch-form-row key="providesSupport" id="providesSupport" label="levert support"
                                      :value="data.providesSupport" @checked="inputSwitchChanged"/>
          <dean-input-switch-form-row key="monitorsDelivery" id="monitorsDelivery" label="volgt levering"
                                      :value="data.monitorsDelivery" @checked="monitorsDeliveryChanged"/>
          <collapse-transition>
            <dean-input-switch-form-row v-if="data.monitorsDelivery" key="managesDelivery" id="managesDelivery"
                                        label="beheert levering" :value="data.managesDelivery"
                                        @checked="inputSwitchChanged"/>
          </collapse-transition>
        </div>
      </dean-two-column-layout>
    </div>
</template>

<script lang="ts">
  import { Vue, Component } from 'vue-property-decorator';
  import {
    deanSectionTitle,
    deanSectionTitleItem,
    saveIcon,
  } from '@deanone/dean-components';
  import DeanFormRow from '@/components/deanComponents/deanFormRow.vue';
  import deanInputSwitchFormRow from '@/components/deanComponents/deanInputSwitchFormRow.vue';
  import DeanTwoColumnLayout from '@/components/deanComponents/deanTwoColumnLayout.vue';
  import { validationMixin } from 'vuelidate';
  import { required } from 'vuelidate/lib/validators';
  import { Validations } from 'vuelidate-property-decorators';
  import { createNotification } from '@/mixins/validations';
  import { CollapseTransition } from 'vue2-transitions';

  @Component({
    components: {
      deanSectionTitle,
      deanSectionTitleItem,
      DeanFormRow,
      DeanTwoColumnLayout,
      deanInputSwitchFormRow,
      CollapseTransition,
    },
    mixins: [validationMixin],
  })
  export default class Details extends Vue {
    // DATA
    public data: any = {
      name: null,
      code: null,
      ownsAgreement: null,
      billsCustomer: null,
      suppliesHardware: null,
      providesSupport: null,
      managesDelivery: null,
      monitorsDelivery: null,
    };
    public saveIcon: any = saveIcon;
    public types: any[] = [];

    // COMPUTED
    @Validations()
    get validations() {
      return {
        data: {
          name: {required},
          code: {required},
        },
      };
    }

    get headerTitle(): string {
      return (this.$route.params.id !== undefined) ? 'partnertype wijzigen' : 'partnertype toevoegen';
    }

    get sideMenu() {
      return {
        content: {
          title: {
            text: 'Type ' + (this.data.name ? this.data.name : 'toevoegen'),
          },
          type: 'themed',
          sections: {
            backToProductTerms: {
              items: {
                products: {
                  title: 'Terug naar partnertypes',
                  name: 'standingData.partnerTypes',
                },
              },
            },
            types: {
              title: 'types',
              items: this.types,
            },
          },
        },
      };
    }

    // METHODS
    public inputSwitchChanged(field: { id: any, value: any }) {
      const key: string = field.id;
      // @ts-ignore-next-line
      this.data[key] = field.value;
    }

    public monitorsDeliveryChanged(field: { id: any, value: any }) {
      this.data.monitorsDelivery = field.value;
      if (!this.data.monitorsDelivery) {
        this.data.managesDelivery = false;
      }
    }

    public save() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.$store.dispatch('setLoadOverlay', true);

        const requestData = {
          id: this.$route.params.id,
          code: this.data.code ? this.data.code : null,
          name: this.data.name ? this.data.name : null,
          ownsAgreement: this.data.ownsAgreement ? this.data.ownsAgreement : false,
          billsCustomer: this.data.billsCustomer ? this.data.billsCustomer : false,
          suppliesHardware: this.data.suppliesHardware ? this.data.suppliesHardware : false,
          providesSupport: this.data.providesSupport ? this.data.providesSupport : false,
          managesDelivery: this.data.managesDelivery ? this.data.managesDelivery : false,
          monitorsDelivery: this.data.monitorsDelivery ? this.data.monitorsDelivery : false,
        };

        this.$store.dispatch('processPartnerAgreementType', requestData).then(
          (response) => {
            this.$toast(this.$store.state.main.notification.messages.dataSaved);
          },
          (error) => {
            this.$toast(this.$store.state.main.notification.messages.couldNotSendData);
          },
        ).finally(() => {
          this.$store.dispatch('updateSideMenu', this.sideMenu);
          this.$store.dispatch('setLoadOverlay', false);
        });
      } else {
        this.$toast(createNotification(this.$v));
      }
    }

    public created() {
      this.$store.dispatch('updateSideMenu', this.sideMenu);
      this.$store.dispatch('setLoadOverlay', true);

      if (this.$route.params.id) {
        this.$store.dispatch('requestPartnerAgreementType', {id: this.$route.params.id})
          .then(
            (response: any) => {
              this.data.id = response.data.data.id;

              this.data.code = response.data.data.code ?
              response.data.data.code : null;

              this.data.name = response.data.data.name ?
              response.data.data.name : null;

              this.data.ownsAgreement = response.data.data.ownsAgreement ?
              response.data.data.ownsAgreement : false;

              this.data.billsCustomer = response.data.data.billsCustomer ?
              response.data.data.billsCustomer : false;

              this.data.suppliesHardware = response.data.data.suppliesHardware ?
              response.data.data.suppliesHardware : false;

              this.data.providesSupport = response.data.data.providesSupport ?
              response.data.data.providesSupport : false;

              this.data.managesDelivery = response.data.data.managesDelivery ?
              response.data.data.managesDelivery : false;

              this.data.monitorsDelivery = response.data.data.monitorsDelivery ?
              response.data.data.monitorsDelivery : false;

              this.transformTypes(response.data.sideMenu.data.types);
            },
            (error: any) => {
              this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
            },
          ).finally(() => {
          this.$store.dispatch('updateSideMenu', this.sideMenu);
          this.$store.dispatch('setLoadOverlay', false);
        });
      }

      this.$store.dispatch('setLoadOverlay', false);
    }

    private transformTypes(responseTypes: any) {
      for (const rType of responseTypes) {
        const newType: any = {};
        newType.title = rType.label;
        newType.name = 'standingData.partnerTypes.edit';
        newType.params = {
          id: rType.href.match('id\\=([a-zA-Z0-9]{8}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{12})')[1],
        };
        this.types.push(newType);
      }
    }
  }
</script>

<style lang="scss" scoped>

</style>
